import { memo, useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import type { Video } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useVideo } from '../../lib/entities';
import { Link, navigate } from '../../lib/router';
import { Icon } from '../../components/ui/Icon';
import { Avatar, cx, VerifiedBadge } from '../../components/ui/primitives';
import { useOpenSheet } from '../../components/ui/Sheet';
import { RichText } from '../../components/RichText';
import { feedAudio, useFeedAudio } from './feedAudio';
import { useVideoActions } from './useVideoActions';
import { CommentsSheet } from './CommentsSheet';
import { VideoMenuSheet } from './VideoMenuSheet';
import styles from './feed.module.css';

interface Props {
  video: Video;
  /** This card is the one on screen. */
  active: boolean;
  /** Within the preload window: attach the source so it buffers. */
  near: boolean;
  /** Whether playback is allowed at all (page visible, no overlay). */
  playbackAllowed: boolean;
  sessionId: string;
}

/**
 * Full-screen video card: autoplay when active, pause otherwise, loop,
 * tap = pause, double-tap = like, scrubbable progress, watch-time signals.
 */
export const VideoCard = memo(function VideoCard({ video: base, active, near, playbackAllowed, sessionId }: Props) {
  const video = useVideo(base);
  const ref = useRef<HTMLVideoElement>(null);
  const { muted, volume } = useFeedAudio();
  const actions = useVideoActions(video);
  const openSheet = useOpenSheet();
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hearts, setHearts] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const watch = useRef({ ms: 0, last: 0, loops: 0 });
  const lastTap = useRef(0);
  const prevT = useRef(0);
  const tapTimer = useRef<ReturnType<typeof setTimeout>>();

  // Play / pause according to visibility.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (active && playbackAllowed && !paused) {
      el.muted = muted;
      const p = el.play();
      p?.catch(() => {
        // Autoplay with sound blocked → fall back to muted autoplay.
        if (!el.muted) {
          feedAudio.setMuted(true);
          el.muted = true;
          void el.play().catch(() => setPaused(true));
        }
      });
    } else {
      el.pause();
    }
  }, [active, playbackAllowed, paused, muted]);

  useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume]);

  // Reset when scrolled away; report watch signals.
  useEffect(() => {
    if (active) {
      watch.current = { ms: 0, last: performance.now(), loops: 0 };
      return;
    }
    setPaused(false);
    const el = ref.current;
    if (el) el.currentTime = 0;
    const w = watch.current;
    if (w.last === 0) return;
    const durMs = (video.durationSec || 1) * 1000;
    const completed = w.loops > 0 || w.ms >= durMs * 0.9;
    const skipped = !completed && w.ms < Math.min(2000, durMs * 0.25);
    void api.post(`/videos/${video.id}/view`, { watchMs: Math.round(w.ms), completed, skipped, sessionId }).catch(() => {});
    watch.current = { ms: 0, last: 0, loops: 0 };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTimeUpdate = () => {
    const el = ref.current;
    if (!el || !el.duration) return;
    // A jump from the end back to the start means the loop completed once.
    if (prevT.current > el.duration * 0.85 && el.currentTime < el.duration * 0.15) watch.current.loops++;
    prevT.current = el.currentTime;
    setProgress(el.currentTime / el.duration);
    const now = performance.now();
    if (active && !el.paused && watch.current.last) watch.current.ms += Math.min(1000, now - watch.current.last);
    watch.current.last = now;
  };

  const burst = (x: number, y: number) => {
    const id = Date.now() + Math.random();
    setHearts((h) => [...h, { id, x, y }]);
    setTimeout(() => setHearts((h) => h.filter((x) => x.id !== id)), 900);
  };

  const onTap = (e: PointerEvent<HTMLDivElement>) => {
    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    if (now - lastTap.current < 280) {
      clearTimeout(tapTimer.current);
      lastTap.current = 0;
      burst(e.clientX - rect.left, e.clientY - rect.top);
      void actions.like(true);
      return;
    }
    lastTap.current = now;
    tapTimer.current = setTimeout(() => {
      if (muted && !feedAudio.get().unlockedOnce) feedAudio.setMuted(false);
      else setPaused((p) => !p);
    }, 280);
  };

  const seekFromPointer = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el?.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const f = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = f * el.duration;
    setProgress(f);
  }, []);

  const openComments = () => openSheet((close) => <CommentsSheet video={video} onClose={close} />);
  const openMenu = () => openSheet((close) => <VideoMenuSheet video={video} onClose={close} />);

  const fit = video.width && video.height && video.width / video.height > 0.7 ? 'contain' : 'cover';

  return (
    <article className={styles.card} aria-label={`Video van ${video.author.displayName}`}>
      {video.thumbnailUrl && <img className={styles.poster} src={video.thumbnailUrl} alt="" style={{ objectFit: fit }} />}
      {near && (
        <video
          ref={ref}
          className={styles.video}
          style={{ objectFit: fit }}
          src={video.videoUrl}
          poster={video.thumbnailUrl ?? undefined}
          preload={active ? 'auto' : 'metadata'}
          playsInline
          loop
          muted={muted}
          onTimeUpdate={onTimeUpdate}
          onWaiting={() => setBuffering(true)}
          onPlaying={() => setBuffering(false)}
        />
      )}

      <div className={styles.tapLayer} onPointerUp={onTap} />
      {hearts.map((h) => (
        <span key={h.id} className={styles.heartBurst} style={{ left: h.x, top: h.y }}>
          <Icon name="heart" filled size={96} />
        </span>
      ))}
      {paused && (
        <span className={styles.pausedIcon} aria-hidden>
          <Icon name="play" size={64} />
        </span>
      )}
      {buffering && active && !paused && <span className={styles.buffering} />}

      {active && muted && !feedAudio.get().unlockedOnce && (
        <button className={styles.unmutePill} onClick={() => feedAudio.setMuted(false)}>
          <Icon name="mute" size={16} /> Tik voor geluid
        </button>
      )}

      <div className={styles.shade} />

      {/* ---- right action rail ---- */}
      <aside className={styles.rail}>
        <div className={styles.railAvatar}>
          <Link to={`/u/${video.author.username}`} aria-label={`Profiel van ${video.author.displayName}`}>
            <Avatar user={video.author} size={48} />
          </Link>
          {!actions.isOwn && !video.viewer.followingAuthor && (
            <button className={styles.followPlus} onClick={actions.follow} aria-label={`Volg ${video.author.displayName}`}>
              <Icon name="plus" size={14} strokeWidth={3} />
            </button>
          )}
        </div>
        <RailButton icon="heart" label="Vind ik leuk" count={video.stats.likes} active={video.viewer.liked} onClick={() => actions.like()} pop />
        <RailButton icon="comment" label="Reacties" count={video.stats.comments} onClick={openComments} />
        <RailButton icon="bookmark" label="Opslaan" count={video.stats.saves} active={video.viewer.saved} onClick={actions.save} />
        <RailButton icon="share" label="Delen" count={video.stats.shares} onClick={actions.share} />
        <RailButton icon="more" label="Meer" onClick={openMenu} />
        {video.track && (
          <Link to={`/sound/${video.track.id}`} className={cx(styles.disc, active && !paused && styles.discSpin)} aria-label={`Geluid: ${video.track.title}`}>
            {video.track.coverUrl ? <img src={video.track.coverUrl} alt="" /> : <Icon name="note" size={18} />}
          </Link>
        )}
      </aside>

      {/* ---- meta ---- */}
      <div className={styles.meta}>
        <Link to={`/u/${video.author.username}`} className={styles.author}>
          @{video.author.username}
          {video.author.verified && <VerifiedBadge />}
          {actions.isOwn ? null : video.viewer.followingAuthor ? <span className={styles.followingTag}>Volgend</span> : null}
        </Link>
        {video.caption && (
          <p className={cx(styles.caption, captionOpen && styles.captionOpen)} onClick={() => setCaptionOpen((o) => !o)}>
            <RichText text={video.caption} />
          </p>
        )}
        {video.track && (
          <button className={styles.soundChip} onClick={() => navigate(`/sound/${video.track!.id}`)}>
            <Icon name="note" size={14} />
            <span className={styles.marquee}>
              <span>
                {video.track.title} — {video.track.artist.name}
              </span>
            </span>
          </button>
        )}
      </div>

      {/* ---- sound control ---- */}
      <div className={styles.soundCtl}>
        <button onClick={() => feedAudio.setMuted(!muted)} aria-label={muted ? 'Geluid aan' : 'Geluid uit'}>
          <Icon name={muted ? 'mute' : 'volume'} size={20} />
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={muted ? 0 : volume}
          aria-label="Volume"
          onChange={(e) => {
            const v = Number(e.target.value);
            feedAudio.setVolume(v);
            if (v > 0 && muted) feedAudio.setMuted(false);
          }}
        />
      </div>

      <div className={styles.progress} onPointerDown={seekFromPointer} onPointerMove={(e) => e.buttons === 1 && seekFromPointer(e)} role="slider" aria-label="Voortgang" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
        <span style={{ transform: `scaleX(${progress})` }} />
      </div>
    </article>
  );
});

function RailButton({ icon, label, count, active, onClick, pop }: { icon: 'heart' | 'comment' | 'bookmark' | 'share' | 'more'; label: string; count?: number; active?: boolean; onClick: () => void; pop?: boolean }) {
  return (
    <button className={cx(styles.railBtn, active && styles.railActive, active && pop && styles.pop)} onClick={onClick} aria-label={label} aria-pressed={active}>
      <span className={styles.railIcon}>
        <Icon name={icon} size={28} filled={active} strokeWidth={1.8} />
      </span>
      {count !== undefined && <span className={styles.railCount}>{formatCount(count)}</span>}
    </button>
  );
}
